/**
 *
 * @author Matthew Rawson @ 16/02/2018 15:16
 * @description
 * @notes
 */
import React, {Component} from "react"
import PropTypes from "prop-types"
import {get, find} from "lodash"
import {TweenMax, Expo, Power2} from "gsap"
import styled from "styled-components"

import id from "@irati/utils/id"
import connect from "@irati/store/connect"
import {H2} from "@irati/ui/src/Headings"
import {minWidthMedia} from "@irati/ui/src/global/media"

import {
  TYPE_DRIVER,
  TYPE_LIKERT,
  INTRO_STARTED, INTRO_COMPLETE, OUTRO_STARTED, OUTRO_COMPLETE, TRIGGER_OUTRO, DIRECTION_BACKWARDS, DIRECTION_FORWARD
} from "@irati/insights/constants/question"
import Likert from "./Likert"
import {Driver} from "./Driver"

import {updateTransitionState} from "../../store/actions/question"


const QuestionTitle = styled(H2)`
  padding-bottom: 20px;
  font-size: 30px;
  line-height: 30px;
  font-family: Lato, sans-serif;
  position: relative;

  ${minWidthMedia.tablet`
    padding-bottom: 30px;
    font-size: 30px;
  `}
`


const TWEEN_SPEED = 0.8
const ANSWER_TWEEN_DELAY = 0.075


class Question extends Component {
  static propTypes = {
    survey: PropTypes.object.isRequired,
    userSurvey: PropTypes.object.isRequired,
    question: PropTypes.object.isRequired,
    answerQuestion: PropTypes.func.isRequired,

    setIntroStarted: PropTypes.func.isRequired,
    setIntroComplete: PropTypes.func.isRequired,
    setOutroStarted: PropTypes.func.isRequired,
    setOutroComplete: PropTypes.func.isRequired,
    setTriggerOutro: PropTypes.func.isRequired,
    questionNumber: PropTypes.number
  }
  static defaultProps = {
    questionNumber: 1
  }


  constructor(props) {
    super(props)
    this.state = {}
    this.triggerIntro = false
  }

  componentDidMount() {
    this.mountTime = +new Date()
  }

  componentDidUpdate(prevProps) {
    /** Trigger the outro when the transition state changes to `TRIGGER_OUTRO` */
    if (this.props.survey.transitionState === TRIGGER_OUTRO) {
      return this.doOutro()
    }
    /** The question has changed */
    if (get(prevProps, "question.id") !== this.props.question.id) {
      this.triggerIntro = true
      this.doIntro()
    }
  }


  doIntro() {
    /** Only run once and when we have the title and answer references */
    if (!(this.triggerIntro && this.title && this.answer)) return

    /** set the flag to disabled any further triggers */
    this.triggerIntro = false

    /** Tween settings by direction, defaulting to forward settings */
    const isBackwards = this.props.survey.transitionDirection === DIRECTION_BACKWARDS
    const fromLeft = isBackwards ? -window.innerWidth : window.innerWidth

    /** Run the tweens */
    TweenMax.fromTo(this.title, TWEEN_SPEED, {left: fromLeft}, {
      left: 0,
      ease: Expo.easeOut,
      delay: 0,
      onStart: this.props.setIntroStarted
    })

    TweenMax.fromTo(this.answer, TWEEN_SPEED, {left: fromLeft}, {
      left: 0,
      ease: Expo.easeOut,
      delay: ANSWER_TWEEN_DELAY,
      onComplete: this.props.setIntroComplete
    })
  }


  doOutro() {
    /** Tween settings by direction, defaulting to forward settings */
    const isBackwards = this.props.survey.transitionDirection === DIRECTION_BACKWARDS
    const left = isBackwards ? window.innerWidth : -window.innerWidth

    /** Run the tweens */
    TweenMax.to(this.title, TWEEN_SPEED, {
      left,
      ease: Power2.easeOut,
      delay: 0,
      onStart: this.props.setOutroStarted
    })

    TweenMax.to(this.answer, TWEEN_SPEED, {
      left,
      ease: Power2.easeOut,
      delay: ANSWER_TWEEN_DELAY,
      onComplete: this.props.setOutroComplete
    })
  }


  /**
   * Inject the time taken to answer the question and call the answerQuestin prop
   *
   * @param answer
   * @param pendSave
   * @param doNotNavigate
   */
  handleAnswerQuestion = (answer, pendSave, doNotNavigate) => {
    /** Trigger the outro routine if not pending */
    if (!pendSave) this.props.setTriggerOutro()

    /** Return the patch promise */
    return this.props.answerQuestion({
      time: +new Date() - this.mountTime,
      ...answer
    }, pendSave, doNotNavigate)
  };


  render() {
    const idNamespace = id("Question", this.props)
    const {question = {}, questionNumber} = this.props
    const submittedAnswer = find(this.props.userSurvey.submittedAnswers, {questionId: question.id})
    return (
      <div id={idNamespace}>

        <QuestionTitle
          large
          id={id("question-title", idNamespace)}
          ref={(ref) => {
            this.title = ref
            this.doIntro()
          }}>{question.text}</QuestionTitle>

        {question.type === TYPE_LIKERT && (
          <Likert
            id={id(`QuestionNumber_${questionNumber}`, idNamespace)}
            ref={(ref) => {
              this.answer = ref
              this.doIntro()
            }}
            submittedAnswer={submittedAnswer}
            answers={question.answers}
            answerQuestion={this.handleAnswerQuestion} />
        )}

        {question.type === TYPE_DRIVER && (
          <Driver
            id={idNamespace}
            ref={(ref) => {
              this.answer = ref && ref.wrappedInstance ? ref.wrappedInstance.ref : ref
              this.doIntro()
            }}
            submittedDriverComments={this.props.userSurvey.submittedDriverComments}
            answerQuestion={this.handleAnswerQuestion} />
        )}
      </div>
    )
  }
}

const mapsStateToProps = ({survey}) => ({survey})

export const mapDispatchToProps = (dispatch) => ({
  setIntroStarted: () => dispatch(updateTransitionState(INTRO_STARTED)),
  setIntroComplete: () => dispatch(updateTransitionState(INTRO_COMPLETE)),
  setOutroStarted: () => dispatch(updateTransitionState(OUTRO_STARTED)),
  setOutroComplete: () => dispatch(updateTransitionState(OUTRO_COMPLETE)),
  setTriggerOutro: () => dispatch(updateTransitionState(TRIGGER_OUTRO, DIRECTION_FORWARD))
})

export default connect(mapsStateToProps, mapDispatchToProps)(Question)
